import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { debug } from "@/lib/debug";

export interface UserWithCredits {
  user_id: string;
  email: string;
  display_name: string | null;
  subscription_tier: "basic" | "solopreneur" | "reseller";
  monthly_credits_used: number;
  custom_credit_limit: number | null;
  bonus_credits: number;
  is_suspended: boolean;
  created_at: string;
  last_reset_at: string;
  is_admin?: boolean;
  status?: string;
}

export interface TierCounts {
  basic: number;
  solopreneur: number;
  reseller: number;
}

export interface CreditTier {
  id: string;
  tier: "basic" | "solopreneur" | "reseller";
  monthly_credit_limit: number;
}

export interface CreditUsageLog {
  id: string;
  user_id: string;
  action: string;
  credits_used: number;
  created_at: string;
  user_email: string;
  display_name: string | null;
}

export interface PaginationInfo {
  page: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
}

export interface AnalyticsSummary {
  totalCreditsUsed: number;
  totalEstimatedCost: string;
  totalActions: number;
  activeUsers: number;
  totalUsers: number;
  periodDays: number;
}

export interface ActionBreakdown {
  action: string;
  count: number;
  totalCredits: number;
  estimatedCost: number;
  description: string;
  avgCreditsPerUse: string;
}

export interface DailyUsage {
  date: string;
  credits: number;
  actions: number;
  cost: number;
}

export interface TopUser {
  userId: string;
  email: string;
  creditsUsed: number;
}

export interface AdminAnalytics {
  summary: AnalyticsSummary;
  actionBreakdown: ActionBreakdown[];
  dailyUsage: DailyUsage[];
  topUsers: TopUser[];
}

// Helper function to ensure fresh session before edge function calls
// Returns the current access token (or null if no valid session)
async function ensureFreshSession(): Promise<string | null> {
  try {
    // First try to get current session
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      // No session at all - try to refresh
      debug.log("[Admin] No session found, attempting refresh...");
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        debug.error("[Admin] Failed to refresh session:", refreshError);
        return null;
      }
      debug.log("[Admin] Session refreshed successfully");
      return refreshData.session.access_token;
    }

    // Check if token is about to expire (within 60 seconds)
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);
    if (expiresAt && expiresAt - now < 60) {
      debug.log("[Admin] Session expiring soon, refreshing...");
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        debug.error("[Admin] Failed to refresh expiring session:", refreshError);
        return null;
      }
      debug.log("[Admin] Session refreshed successfully");
      return refreshData.session.access_token;
    }

    return session.access_token;
  } catch (error) {
    debug.error("[Admin] Error ensuring fresh session:", error);
    return null;
  }
}

// Helper function to check if error is auth-related
function isAuthError(error: unknown, errorBody?: { error?: string }): boolean {
  // Check error body from parsed response
  if (errorBody?.error) {
    const msg = errorBody.error.toLowerCase();
    if (msg.includes("auth") || msg.includes("session") || msg.includes("unauthorized") || msg.includes("missing")) {
      return true;
    }
  }
  
  // Check if it's a FunctionsHttpError with 401/403 status
  if (error instanceof FunctionsHttpError) {
    const status = error.context?.status;
    if (status === 401 || status === 403) {
      return true;
    }
  }
  
  // Check error message
  if (error instanceof Error) {
    const msg = error.message?.toLowerCase() || "";
    if (msg.includes("auth") || msg.includes("session") || msg.includes("unauthorized")) {
      return true;
    }
  }
  
  return false;
}

// Helper function to invoke edge function with retry and exponential backoff
async function invokeWithRetry<T>(
  functionName: string,
  body: Record<string, unknown>,
  maxRetries = 3,
  showToastOnError = true
): Promise<{ data: T | null; error: Error | null }> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    debug.log(`[Admin] Calling ${functionName}, attempt ${attempt + 1}/${maxRetries}`);

    // Ensure fresh session before each attempt and explicitly pass the token
    const accessToken = await ensureFreshSession();
    if (!accessToken) {
      debug.log(`[Admin] Session invalid on attempt ${attempt + 1}`);
      if (attempt === maxRetries - 1) {
        return { data: null, error: new Error("Session expired. Please log in again.") };
      }
      const delay = 500 * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
      continue;
    }

    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (error) {
        debug.log(`[Admin] Error from ${functionName}:`, error);

        let errorBody: { error?: string } | undefined;

        // Try to parse error body from FunctionsHttpError
        if (error instanceof FunctionsHttpError) {
          try {
            const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } })?.context;
            if (ctx?.json) {
              errorBody = await ctx.json();
              debug.log(`[Admin] Parsed error body:`, errorBody);
            }
          } catch {
            // ignore
          }
        }

        // Check if it's an auth error
        if (isAuthError(error, errorBody) && attempt < maxRetries - 1) {
          debug.log(
            `[Admin] Auth error detected on attempt ${attempt + 1}, refreshing session and retrying...`
          );
          await supabase.auth.refreshSession();
          const delay = 500 * Math.pow(2, attempt);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        const errorMessage = errorBody?.error || error.message || "Unknown error";
        if (showToastOnError) {
          toast.error(`Admin request failed: ${functionName} - ${errorMessage}`);
        }
        return { data: null, error: new Error(`${functionName}: ${errorMessage}`) };
      }

      // Check if data itself contains an error (edge function returned JSON with error)
      if (data?.error) {
        debug.log(`[Admin] Data contains error:`, data.error);
        const errorMessage = typeof data.error === "string" ? data.error : "Unknown error";

        if (isAuthError(null, { error: errorMessage }) && attempt < maxRetries - 1) {
          debug.log(`[Admin] Auth error in data on attempt ${attempt + 1}, retrying...`);
          await supabase.auth.refreshSession();
          const delay = 500 * Math.pow(2, attempt);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        if (showToastOnError) {
          toast.error(`Admin request failed: ${functionName} - ${errorMessage}`);
        }
        return { data: null, error: new Error(`${functionName}: ${errorMessage}`) };
      }

      debug.log(`[Admin] ${functionName} succeeded`);
      return { data: data as T, error: null };
    } catch (err) {
      debug.error(`[Admin] Exception calling ${functionName}:`, err);
      if (attempt === maxRetries - 1) {
        const errMsg = err instanceof Error ? err.message : "Unknown error";
        if (showToastOnError) {
          toast.error(`Admin request failed: ${functionName} - ${errMsg}`);
        }
        return { data: null, error: new Error(`${functionName}: ${errMsg}`) };
      }
      const delay = 500 * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return { data: null, error: new Error("Max retries exceeded") };
}

export function useAdmin() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserWithCredits[]>([]);
  const [creditTiers, setCreditTiers] = useState<CreditTier[]>([]);
  const [usageLogs, setUsageLogs] = useState<CreditUsageLog[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    perPage: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [usersLoading, setUsersLoading] = useState(false);
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [tierCounts, setTierCounts] = useState<{ basic: number; solopreneur: number; reseller: number }>({
    basic: 0,
    solopreneur: 0,
    reseller: 0,
  });

  // Check if current user is admin
  useEffect(() => {
    async function checkAdminStatus() {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      setIsAdmin(!!data && !error);
      setLoading(false);
    }

    checkAdminStatus();
  }, [user]);

  // Fetch all users with their credit info (admin only)
  const fetchUsers = useCallback(async (page = 1, perPage = 10, search = "", tierFilter = "") => {
    if (!isAdmin) return;

    setUsersLoading(true);
    try {
      const { data, error } = await invokeWithRetry<{
        users: UserWithCredits[];
        pagination: PaginationInfo;
        tierCounts: { basic: number; solopreneur: number; reseller: number };
      }>("get-admin-users", { page, perPage, search, tierFilter }, 3, false);
      
      if (error) {
        debug.error("Error fetching users:", error);
        toast.error(`Failed to fetch users: ${error.message}`);
        return;
      }

      if (data?.users) {
        setUsers(data.users);
      }
      if (data?.pagination) {
        setPagination(data.pagination);
      }
      if (data?.tierCounts) {
        setTierCounts(data.tierCounts);
      }
    } catch (err) {
      debug.error("Error fetching users:", err);
      toast.error("Failed to fetch users");
    } finally {
      setUsersLoading(false);
    }
  }, [isAdmin]);

  // Fetch credit tier configurations
  const fetchCreditTiers = async () => {
    const { data, error } = await supabase
      .from("credit_tiers")
      .select("*")
      .order("monthly_credit_limit", { ascending: true });

    if (error) {
      debug.error("Error fetching credit tiers:", error);
      return;
    }

    setCreditTiers(data as CreditTier[]);
  };

  // Fetch usage logs (admin only) - uses edge function to get user emails
  const fetchUsageLogs = async (limit = 100) => {
    if (!isAdmin) return;

    try {
      const { data, error } = await invokeWithRetry<{ logs: CreditUsageLog[] }>(
        "get-admin-usage-logs",
        { limit, offset: 0 },
        3,
        false
      );

      if (error) {
        debug.error("Error fetching usage logs:", error);
        toast.error(`Failed to fetch usage logs: ${error.message}`);
        return;
      }

      if (data?.logs) {
        setUsageLogs(data.logs);
      }
    } catch (err) {
      debug.error("Error fetching usage logs:", err);
    }
  };

  // Fetch analytics data
  const fetchAnalytics = useCallback(async (daysBack = 30) => {
    if (!isAdmin) return;

    setAnalyticsLoading(true);
    try {
      const { data, error } = await invokeWithRetry<AdminAnalytics>(
        "get-admin-analytics",
        { daysBack },
        3,
        false
      );

      if (error) {
        debug.error("Error fetching analytics:", error);
        toast.error(`Failed to fetch analytics: ${error.message}`);
        return;
      }

      if (data) {
        setAnalytics(data);
      }
    } catch (err) {
      debug.error("Error fetching analytics:", err);
      toast.error("Failed to fetch analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [isAdmin]);

  // Update user's subscription tier
  const updateUserTier = async (userId: string, tier: "basic" | "solopreneur" | "reseller") => {
    const { error } = await supabase
      .from("user_credits")
      .update({ subscription_tier: tier })
      .eq("user_id", userId);

    if (error) {
      debug.error("Error updating user tier:", error);
      return false;
    }

    await fetchUsers();
    return true;
  };

  // Update user's custom credit limit
  const updateUserCreditLimit = async (userId: string, limit: number | null) => {
    const { error } = await supabase
      .from("user_credits")
      .update({ custom_credit_limit: limit })
      .eq("user_id", userId);

    if (error) {
      debug.error("Error updating credit limit:", error);
      return false;
    }

    await fetchUsers();
    return true;
  };

  // Suspend/activate user
  const toggleUserSuspension = async (userId: string, suspend: boolean) => {
    const { error } = await supabase
      .from("user_credits")
      .update({ is_suspended: suspend })
      .eq("user_id", userId);

    if (error) {
      debug.error("Error toggling user suspension:", error);
      return false;
    }

    await fetchUsers();
    return true;
  };

  // Reset user's monthly credits
  const resetUserCredits = async (userId: string) => {
    const { error } = await supabase
      .from("user_credits")
      .update({ 
        monthly_credits_used: 0, 
        last_reset_at: new Date().toISOString() 
      })
      .eq("user_id", userId);

    if (error) {
      debug.error("Error resetting user credits:", error);
      return false;
    }

    await fetchUsers();
    return true;
  };

  // Update tier default limits
  const updateTierLimit = async (tierId: string, newLimit: number) => {
    const { error } = await supabase
      .from("credit_tiers")
      .update({ monthly_credit_limit: newLimit })
      .eq("id", tierId);

    if (error) {
      debug.error("Error updating tier limit:", error);
      return false;
    }

    await fetchCreditTiers();
    return true;
  };

  // Delete user
  const deleteUser = async (userId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { user_id: userId }
      });

      if (error) {
        debug.error("Error deleting user:", error);
        return false;
      }

      if (data?.error) {
        debug.error("Delete user error:", data.error);
        return false;
      }

      await fetchUsers();
      return true;
    } catch (err) {
      debug.error("Error deleting user:", err);
      return false;
    }
  };

  // Update user (display name, email, tier, custom limit, bonus credits)
  const updateUser = async (userId: string, updates: {
    displayName?: string;
    email?: string;
    tier?: "basic" | "solopreneur" | "reseller";
    customLimit?: number | null;
    bonusCredits?: number;
    password?: string;
  }) => {
    try {
      const { tier, displayName, customLimit, bonusCredits, ...rest } = updates;
      const body: Record<string, unknown> = { user_id: userId, ...rest };
      if (displayName !== undefined) body.display_name = displayName;
      if (tier !== undefined) body.subscription_tier = tier;
      if (customLimit !== undefined) body.custom_limit = customLimit;
      if (bonusCredits !== undefined) body.add_bonus_credits = bonusCredits;
      const { data, error } = await supabase.functions.invoke("update-user", { body });

      if (error) {
        debug.error("Error updating user:", error);
        return { success: false, error: error.message };
      }

      if (data?.error) {
        debug.error("Update user error:", data.error);
        return { success: false, error: data.error };
      }

      await fetchUsers();
      return { success: true };
    } catch (err) {
      debug.error("Error updating user:", err);
      return { success: false, error: "Failed to update user" };
    }
  };

  // Add bonus credits to a user
  const addBonusCredits = async (userId: string, amount: number): Promise<{ success: boolean; error?: string }> => {
    try {
      // Get current bonus credits
      const { data: currentData, error: fetchError } = await supabase
        .from("user_credits")
        .select("bonus_credits")
        .eq("user_id", userId)
        .single();

      if (fetchError) {
        debug.error("Error fetching current bonus credits:", fetchError);
        return { success: false, error: "Failed to fetch current credits" };
      }

      const newTotal = (currentData?.bonus_credits || 0) + amount;

      const { error } = await supabase
        .from("user_credits")
        .update({ bonus_credits: newTotal })
        .eq("user_id", userId);

      if (error) {
        debug.error("Error adding bonus credits:", error);
        return { success: false, error: "Failed to add bonus credits" };
      }

      await fetchUsers();
      return { success: true };
    } catch (err) {
      debug.error("Error adding bonus credits:", err);
      return { success: false, error: "Failed to add bonus credits" };
    }
  };

  // Pause or activate a user account via profiles.status
  const pauseUserAccount = async (userId: string, pause: boolean): Promise<boolean> => {
    const { error } = await supabase
      .from("profiles")
      .update({ status: pause ? "paused" : "active" })
      .eq("user_id", userId);

    if (error) {
      debug.error("Error updating account status:", error);
      return false;
    }

    await fetchUsers();
    return true;
  };

  return {
    isAdmin,
    loading,
    users,
    creditTiers,
    usageLogs,
    pagination,
    usersLoading,
    analytics,
    analyticsLoading,
    tierCounts,
    fetchUsers,
    fetchCreditTiers,
    fetchUsageLogs,
    fetchAnalytics,
    updateUserTier,
    updateUserCreditLimit,
    toggleUserSuspension,
    resetUserCredits,
    updateTierLimit,
    deleteUser,
    updateUser,
    addBonusCredits,
    pauseUserAccount,
  };
}
